import { fromJS } from 'immutable';
import _ from 'lodash';
import { orderSkillParts } from '@utils/skillUtils';
import { TYPES } from './actions';
import courseProgressConsts from '../../helpers/courseProgressConsts';

const skillpartOrder = {
  LESSON: 0,
  PRACTICE: 1,
  EXERCISE: 1,
  POLL: 2,
};

const {
  AVAILABLE, CURRENT, COMPLETED, PREVIOUS, LOCKED,
} = courseProgressConsts;
// The initial state of the Course router
export const initialState = fromJS({
  courses: [],
  loading: true,
  loadedOnce: false,
  error: null,
  activityContext: {},
});

const addStatus = (status) => (entity) => ({
  ...entity,
  status,
});

const addAvailableStatus = (entity) => ({
  ...entity,
  status: AVAILABLE,
});

const addLockedStatus = (entity) => ({
  ...entity,
  status: LOCKED,
});

const convertToLocation = (practice, practiceIndex) => (entity, skillpartIndex) => ({
  entity: practice ? { ...entity, isAccessible: practice.isAccessible } : entity,
  activityId: practice ? practice.id : entity.id,
  skillpartIndex: _.isUndefined(practiceIndex) ? skillpartIndex : practiceIndex,
  exerciseIndex: _.isUndefined(practiceIndex) ? undefined : skillpartIndex,
  skillpartName: practice ? practice.name : entity.name,
  skillpartType: practice ? practice.type.toLowerCase() : entity.type.toLowerCase(),
  paused: practice ? skillpartIndex > 0 : false,
});

const parseSkill = (skill, status, focusedActivityId) => {
  let locationInCourse = {
    skillpartType: null,
    skillpartName: '',
    skillpartIndex: null,
    paused: false,
  };
  const parsedSkill = _.cloneDeep(skill);
  parsedSkill.status = status;

  const mapWithStatus = addStatus(status);
  if (status === LOCKED) {
    parsedSkill.Lessons = parsedSkill.Lessons.map(addLockedStatus);
    parsedSkill.Polls = parsedSkill.Polls.map(addLockedStatus);
    parsedSkill.Practices = parsedSkill.Practices.map((practice) => {
      const lockedPractice = _.cloneDeep(practice);
      const practiceStatus = LOCKED;
      lockedPractice.Exercises = lockedPractice.Exercises.map(addLockedStatus);
      return {
        ...lockedPractice,
        status: practiceStatus,
      };
    });
  } else if (status === COMPLETED || status === PREVIOUS) {
    parsedSkill.Lessons = parsedSkill.Lessons.map(mapWithStatus);
    parsedSkill.Polls = parsedSkill.Polls.map(mapWithStatus);
    parsedSkill.Practices = parsedSkill.Practices.map((practice) => ({
      ...practice,
      status,
      Exercises: practice.Exercises.map(mapWithStatus),
    }));
  } else if (status === AVAILABLE) {
    parsedSkill.Lessons = parsedSkill.Lessons.map(addAvailableStatus);
    parsedSkill.Polls = parsedSkill.Polls.map(addAvailableStatus);
    parsedSkill.Practices = parsedSkill.Practices.map((practice) => ({
      ...practice,
      status: AVAILABLE,
      Exercises: practice.Exercises.map(addAvailableStatus),
    }));
  } else if (status === CURRENT) {
    const skillParts = [
      ...parsedSkill.Lessons.map(convertToLocation()),
      ..._.flatten(parsedSkill.Practices.map((practice, skillpartIndex) => practice.Exercises.map(convertToLocation(practice, skillpartIndex)))),
      ...parsedSkill.Polls.map(convertToLocation()),
    ];
    const currentSkillpart = focusedActivityId === undefined
      ? skillParts.find((item) => !item.entity.hasPassed && item.entity.isAccessible)
      : skillParts.find((item) => item.activityId === focusedActivityId);
    const resolvedFocusedActivityId = currentSkillpart?.activityId;
    const children = parsedSkill.children || [];
    const currentChildIndex = children.findIndex(
      ({ id }) => id === resolvedFocusedActivityId
    );
    const isBeforeCurrent = (id) => {
      const childIndex = children.findIndex((child) => child.id === id);
      return childIndex >= 0 && currentChildIndex >= 0 && childIndex < currentChildIndex;
    };
    locationInCourse = _.omit(currentSkillpart, ['activityId', 'entity']);

    parsedSkill.Lessons = parsedSkill.Lessons.map((lesson, index) => {
      const lessonStatus = getSkillpartStatus(
        lesson,
        index,
        currentSkillpart,
        resolvedFocusedActivityId,
        isBeforeCurrent(lesson.id)
      );
      return ({
        ...lesson,
        status: lessonStatus, // change to COMPLETED to bypass lesson
      });
    });
    parsedSkill.Practices = parsedSkill.Practices.map((practice, index) => {
      const updatedPractice = _.cloneDeep(practice);
      const practiceStatus = getSkillpartStatus(
        practice,
        index,
        currentSkillpart,
        resolvedFocusedActivityId,
        isBeforeCurrent(practice.id)
      );
      if (practiceStatus === CURRENT) {
        updatedPractice.Exercises = updatedPractice.Exercises.map((exercise, exerciseIndex) => {
          const exerciseStatus = getSkillpartStatus(exercise, exerciseIndex, currentSkillpart);
          return ({
            ...exercise,
            status: exerciseStatus,
          });
        });
      } else {
        const mapExercisesWithStatus = addStatus(practiceStatus);
        updatedPractice.Exercises = updatedPractice.Exercises.map(mapExercisesWithStatus);
      }
      return {
        ...updatedPractice,
        status: practiceStatus, // change to CURRENT to get to practice
      };
    });
    parsedSkill.Polls = parsedSkill.Polls.map((poll, index) => {
      const pollStatus = getSkillpartStatus(
        poll,
        index,
        currentSkillpart,
        resolvedFocusedActivityId,
        isBeforeCurrent(poll.id)
      );

      return ({
        ...poll,
        status: pollStatus,
      });
    });
  }
  parsedSkill.orderedSkillParts = orderSkillParts(parsedSkill);
  return { parsedSkill, locationInCourse };
};

const getSkillpartStatus = (
  skillpart,
  index,
  currentSkillpart,
  focusedActivityId,
  isBeforeCurrent = false
) => {
  if (skillpart.type !== 'EXERCISE') {
    if (focusedActivityId && skillpart.id === focusedActivityId) {
      return CURRENT;
    } if (isBeforeCurrent) {
      return PREVIOUS;
    } if (skillpart.isAccessible) {
      return AVAILABLE;
    }
    return LOCKED;
  }
  const currentPartIndex = currentSkillpart.exerciseIndex;
  if (index > currentPartIndex) {
    return LOCKED;
  } if (index < currentPartIndex) {
    return PREVIOUS;
  }
  return CURRENT;
};

const getSkillStatus = (skill, skillIndex, course, previousSkillStatus) => {
  const hasPaidSubscription = !course.isDefault;
  const nextSkillIndex = course.nextCourseActivity
    ? course.Skills.findIndex(({ id }) => id === course.nextCourseActivity.skillId)
    : -1;

  if (nextSkillIndex >= 0) {
    if (skillIndex < nextSkillIndex) {
      return PREVIOUS;
    }
    if (skillIndex === nextSkillIndex) return CURRENT;
    return LOCKED;
  }
  if (course.nextCourseActivity === null) {
    return skill.progress === 100 ? COMPLETED : LOCKED;
  }
  if (hasPaidSubscription && skill.free) {
    return COMPLETED;
  }
  if (!skill.subscribed || (skillIndex > 0 && previousSkillStatus !== COMPLETED)) {
    return LOCKED;
  } if (skill.progress === 100) {
    return COMPLETED;
  }

  return CURRENT;
};

const parseCourses = (courses) => {
  let previousSkillStatus = null;
  const parsedCourses = courses.map((course, courseIndex) => {
    let locationInCourse = {
      skillpartType: null,
      skillpartName: '',
      skillpartIndex: null,
      paused: false,
    };
    const parsedSkills = course.Skills.map((skill, skillIndex) => {
      const status = getSkillStatus(skill, skillIndex, course, previousSkillStatus);
      previousSkillStatus = status;
      const focusedActivityId = course.nextCourseActivity
        ? (course.nextCourseActivity.skillId === skill.id ? course.nextCourseActivity.entityId : null)
        : undefined;
      const parseResult = parseSkill(skill, status, focusedActivityId);

      if (status === CURRENT && parseResult.locationInCourse.skillpartType) {
        locationInCourse = parseResult.locationInCourse;
        locationInCourse.skillIndex = skillIndex;
        locationInCourse.skillName = skill.name;
        locationInCourse.path = `${courseIndex}/skill${skillIndex}/${locationInCourse.skillpartType}${locationInCourse.skillpartIndex}`;
        if (locationInCourse.skillpartType === 'practice') {
          locationInCourse.path = `${locationInCourse.path}/exercise${locationInCourse.exerciseIndex}`;
        }
      }
      return parseResult.parsedSkill;
    });
    return {
      ...course,
      locationInCourse,
      Skills: parsedSkills,
    };
  });
  return parsedCourses;
};

function courseRouterReducer(state = initialState, action) {
  switch (action.type) {
    case TYPES.GET_COURSES: {
      return state
        .set('loading', true)
        .set('error', null);
    }
    case TYPES.SET_COURSES: {
      const parsedCourses = parseCourses(action.courses);
      return state
        .set('courses', fromJS(parsedCourses))
        .set('loading', false)
        .set('loadedOnce', true)
        .set('error', null);
    }
    case TYPES.SET_COURSES_FAILED: {
      return state
        .set('courses', fromJS([]))
        .set('loading', false)
        .set('loadedOnce', true)
        .set('error', action.error || 'CONNECTION_ERROR');
    }
    case TYPES.SET_ACTIVITY_CONTEXT:

      return state.set('activityContext', action.activityContext);
    case TYPES.MARK_FEEDBACK_AS_SEEN:
      return state.set('loading', true);
    case TYPES.MARK_FEEDBACK_AS_SEEN_SUCCEED:
      return state.set('loading', false);
    case TYPES.MARK_FEEDBACK_AS_SEEN_FAILED:
      return state.set('loading', false);
    default:
      return state;
  }
}

export default courseRouterReducer;
